import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useQuery, useLazyQuery } from '@apollo/client';
import { isAuthTokenValid } from '../helper/token';
import { USER_PROFILE, USER_USERNAME_VERIFY } from '../helper/gql';
import './Profile.css';

export const Profile: React.FC = () => {
  const history = useHistory();

  useEffect(() => {
    if (!isAuthTokenValid()) {
      history.push('/alpha/login');
    }
  }, [history]);

  const { data, loading, error } = useQuery(USER_PROFILE, {
    skip: !isAuthTokenValid(),
  });

  useEffect(() => {
    if (error) {
      history.push('/alpha/login');
    }
  }, [error, history]);

  const [usernameInput, setUsernameInput] = useState('');
  const [isEditingUsername, setIsEditingUsername] = useState(false);
  const [verifyResult, setVerifyResult] = useState<boolean | null>(null);
  const [validationError, setValidationError] = useState<string | null>(null);

  const [verifyUsername, { loading: verifying }] = useLazyQuery(USER_USERNAME_VERIFY, {
    onCompleted: (result) => {
      setVerifyResult(result?.userUsernameVerify?.available ?? null);
    },
  });

  const handleUsernameEdit = (handle: string | null) => {
    setUsernameInput(handle || '');
    setVerifyResult(null);
    setValidationError(null);
    setIsEditingUsername(true);
  };

  const handleVerify = () => {
    const value = usernameInput.trim();
    setVerifyResult(null);
    setValidationError(null);

    if (!value) return;

    if (value === (handle || '')) {
      setValidationError('Username must be different from the current one.');
      return;
    }
    if (value.length < 8) {
      setValidationError('Username must be at least 8 characters.');
      return;
    }
    if (!/^[a-zA-Z0-9._-]+$/.test(value)) {
      setValidationError('Username may only contain letters, numbers, ".", "_", or "-".');
      return;
    }
    if (/^[.\-0-9]/.test(value)) {
      setValidationError('Username cannot start with ".", "-", or a number.');
      return;
    }

    verifyUsername({ variables: { input: { username: value } } });
  };

  if (loading || !data?.userProfile) {
    return <div className="profile-loading">Loading...</div>;
  }

  const { email, handle, memberSince } = data.userProfile;
  const displayName = handle || email;

  const initials = displayName
    .split(/[@.\s]/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s: string) => s[0].toUpperCase())
    .join('');

  return (
    <div className="profile-page">
      <button className="profile-back-link" onClick={() => {
        const returnPath = sessionStorage.getItem('profileReturnPath') || '/dashboard';
        sessionStorage.removeItem('profileReturnPath');
        history.push(returnPath);
      }}>
        &larr; Back
      </button>
      <div className="profile-container">
        <div className="profile-header">
          <div className="profile-avatar">{initials}</div>
          <h1 className="profile-name">{displayName}</h1>
          <p className="profile-email">{email}</p>
        </div>

        <div className="profile-sections">
          <section className="profile-section">
            <h2 className="profile-section-title">Account Information</h2>
            <div className="profile-field">
              <label>Email</label>
              <span>{email}</span>
            </div>
            <div className="profile-field profile-field--username">
              <label>Username</label>
              {isEditingUsername ? (
                <div className="profile-username-edit">
                  <input
                    className="profile-username-input"
                    type="text"
                    value={usernameInput}
                    onChange={(e) => {
                      setUsernameInput(e.target.value);
                      setVerifyResult(null);
                      setValidationError(null);
                    }}
                    placeholder="Enter username"
                    autoFocus
                  />
                  <button
                    className="profile-verify-btn"
                    onClick={handleVerify}
                    disabled={verifying || !usernameInput.trim()}
                  >
                    {verifying ? 'Checking...' : 'Verify'}
                  </button>
                  <button
                    className="profile-cancel-btn"
                    onClick={() => {
                      setIsEditingUsername(false);
                      setVerifyResult(null);
                      setValidationError(null);
                    }}
                  >
                    Cancel
                  </button>
                  {validationError && (
                    <span className="profile-verify-result profile-verify-result--error">
                      {validationError}
                    </span>
                  )}
                  {verifyResult !== null && (
                    <span className={`profile-verify-result profile-verify-result--${verifyResult ? 'available' : 'taken'}`}>
                      {verifyResult ? 'Available' : 'Not available'}
                    </span>
                  )}
                </div>
              ) : (
                <div className="profile-username-display">
                  <span>{handle || '-'}</span>
                  <button
                    className="profile-edit-btn"
                    onClick={() => handleUsernameEdit(handle)}
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
            <div className="profile-field">
              <label>Member since</label>
              <span>{memberSince ? new Date(memberSince).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '-'}</span>
            </div>
          </section>

          <section className="profile-section">
            <h2 className="profile-section-title">Preferences</h2>
            <div className="profile-field">
              <label>Default view</label>
              <span>Card</span>
            </div>
            <div className="profile-field">
              <label>Notifications</label>
              <span>Enabled</span>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};